import { useEffect, useId, useMemo, useRef, useState } from "react";
import TRAIcon from "../icons/TRAIcon";
import { cn } from "../utils/cn";
import v from "./traVisual.module.css";

/**
 * Profile Visual v2 custom select (listbox) — same field chrome as edit-profile inputs.
 *
 * @param {{
 *   value: string;
 *   onChange: (value: string) => void;
 *   options: { id: string; label: string; prefix?: string; shortLabel?: string }[];
 *   placeholder?: string;
 *   ariaLabel: string;
 *   allowClear?: boolean;
 *   searchable?: boolean;
 *   searchPlaceholder?: string;
 *   disabled?: boolean;
 *   error?: boolean;
 *   id?: string;
 *   className?: string;
 * }} props
 */
export default function TRAProfileSelect({
  value,
  onChange,
  options,
  placeholder = "Choose…",
  ariaLabel,
  allowClear = true,
  searchable = false,
  searchPlaceholder = "Search…",
  disabled = false,
  error = false,
  id: idProp,
  className = "",
  tone = "field",
}) {
  const autoId = useId();
  const triggerId = idProp || autoId;
  const searchId = `${triggerId}-search`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    if (searchable) {
      const id = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open, searchable]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      const hay = `${o.label} ${o.id} ${o.shortLabel || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query, searchable]);

  const selected = options.find((o) => o.id === value);

  const formatOption = (o, { compact = false } = {}) => {
    const text = compact && o.shortLabel ? o.shortLabel : o.label;
    return (
      <>
        {o.prefix ? <span className={v.profileSelectPrefix}>{o.prefix}</span> : null}
        <span className={v.profileSelectText}>{text}</span>
      </>
    );
  };

  const triggerLabel = selected ? formatOption(selected, { compact: true }) : placeholder;
  const hasVal = Boolean(value);

  const close = () => setOpen(false);

  return (
    <div className={cn(v.profileSelectWrap, className)} ref={wrapRef}>
      <button
        type="button"
        id={triggerId}
        className={cn(
          v.profileSelectTrigger,
          tone === "pill" && v.profileSelectTriggerPill,
          hasVal && v.profileSelectTriggerOn,
          error && v.profileSelectTriggerErr
        )}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
      >
        {triggerLabel}
      </button>
      <TRAIcon name="chevronDown" size="sm" className={v.profileSelectChev} />
      {open && !disabled ? (
        <div
          className={cn(v.profileSelectMenu, tone === "pill" && v.profileSelectMenuPill)}
          role="listbox"
          aria-labelledby={triggerId}
        >
          {searchable ? (
            <div className={v.profileSelectSearchWrap}>
              <input
                ref={searchRef}
                id={searchId}
                type="search"
                className={v.profileSelectSearch}
                value={query}
                placeholder={searchPlaceholder}
                aria-label={`${ariaLabel} search`}
                autoComplete="off"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    close();
                  }
                }}
              />
            </div>
          ) : null}
          {allowClear ? (
            <button
              type="button"
              role="option"
              className={cn(v.profileSelectOpt, v.profileSelectOptMuted)}
              onClick={() => {
                onChange("");
                close();
              }}
            >
              {placeholder}
            </button>
          ) : null}
          {filteredOptions.length === 0 ? (
            <p className={v.profileSelectEmpty} role="status">
              No matches
            </p>
          ) : (
            filteredOptions.map((o) => (
              <button
                key={o.id}
                type="button"
                role="option"
                aria-selected={o.id === value}
                className={cn(v.profileSelectOpt, o.id === value && v.profileSelectOptOn)}
                onClick={() => {
                  onChange(o.id);
                  close();
                }}
              >
                {formatOption(o)}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
